import "./style.css";
import "./competiterList.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Dropdown,
  Image,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Form,
  Tag,
  message,
} from "antd";
import moment from "moment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isoWeek from "dayjs/plugin/isoWeek";
import {
  EnvironmentOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { FaBed, FaBath, FaKitchenSet } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  GreenNotify,
  RedNotify,
  useFirebaseUpload,
} from "../../helper/helper";
import Loader from "../../components/loader/loader";
import PageHeader from "../../components/pageHeader/PageHeader";
import { statusColor } from "../../helper/statusMeta";
import { normalizeCoordinates } from "../../helper/coordinates";
import {
  clearDraft,
  draftSavedLabel,
  readDraft,
  stripItemFiles,
  useBeforeUnloadWarning,
  useDraftSaver,
} from "../../helper/formDrafts";
import { isAssignableInspector } from "../../helper/userDisplay";
import { validateChecklistItems } from "../checklists/checklistValidation";
import CreateTaskModal from "./CreateTaskModal";
import AssignInspectors from "../../components/AssignInspectors/AssignInspectors";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

// First Monday of next week, as "YYYY-MM-DD" in the given timezone — seeds a
// property's cadence so weeklyInspectionReset starts on the next local Monday.
const nextMondayInTz = (tz) =>
  dayjs()
    .tz(tz || "America/Denver")
    .startOf("isoWeek")
    .add(1, "week")
    .format("YYYY-MM-DD");

const statusOptions = ["pending", "in progress", "completed"];

const bathOptions = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const kitchenOptions = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const bedOptions = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const porchOptions = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const lawnOptions = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];

const CompetitorList = () => {
  const [loading, setLoading] = useState(true);
  const [isloading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  // null = show all; otherwise filter to properties this inspector is assigned to.
  const [inspectorFilter, setInspectorFilter] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [taskData, setTasksData] = useState([]);
  const [signUpModal, setSignUpModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  // Selection checkboxes are hidden until the user enables "Select multiple".
  const [selectMode, setSelectMode] = useState(false);
  const [bulkInspector, setBulkInspector] = useState(null);
  const [update, setUpdate] = useState(false);
  const [form] = Form.useForm();
  const [questionaire, setQuestionaire] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [saveTemplateModal, setSaveTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [fileList, setFileList] = useState([]);
  const [coordinates, setCoordinates] = useState(null);

  // ── Draft autosave (create flow only) ───────────────────────────────────
  // Persist the in-progress property to localStorage so an accidental close /
  // refresh / failed save can be recovered. The cover photo uploads on pick, so
  // its URL is drafted too and restored automatically (the raw File can't be —
  // but by save time it's already a URL).
  const PROPERTY_DRAFT_KEY = "property:new";
  const restorePendingRef = useRef(false); // true while the restore prompt is up
  const restoreCheckedRef = useRef(false); // only prompt once per open

  const { saveSoon } = useDraftSaver(PROPERTY_DRAFT_KEY, {
    enabled: signUpModal && !editingTask,
    buildSnapshot: () => ({
      values: form.getFieldsValue(true),
      questionaire: stripItemFiles(questionaire),
      coordinates,
      // Only an already-uploaded URL is draftable (the raw File can't be). The
      // photo uploads on pick (uploadProps.onChange), so this is set by then.
      imageUrl: fileList?.[0]?.url || null,
    }),
    isMeaningful: (s) =>
      (s.questionaire?.length || 0) > 0 ||
      !!s.coordinates ||
      !!s.imageUrl ||
      Object.values(s.values || {}).some(
        (v) => typeof v === "string" && v.trim() !== "",
      ),
  });

  // Autosave on field edits (wired to the form's onValuesChange) and whenever
  // the checklist/coordinates change — but never while a restore prompt is open.
  const triggerDraftSave = useCallback(() => {
    if (restorePendingRef.current) return;
    saveSoon();
  }, [saveSoon]);

  // Warn on tab refresh/close while a new property has unsaved work.
  useBeforeUnloadWarning(
    () => signUpModal && !editingTask && !!readDraft(PROPERTY_DRAFT_KEY),
  );

  useEffect(() => {
    triggerDraftSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionaire, coordinates]);

  // On opening the create modal, offer to restore a saved draft (once).
  useEffect(() => {
    if (!signUpModal || editingTask) {
      restoreCheckedRef.current = false;
      return;
    }
    if (restoreCheckedRef.current) return;
    restoreCheckedRef.current = true;

    const draft = readDraft(PROPERTY_DRAFT_KEY);
    if (!draft?.data) return;

    restorePendingRef.current = true;
    Modal.confirm({
      title: "Restore unfinished property?",
      content: `You have an unsaved property from ${draftSavedLabel(
        draft.savedAt,
      )}. Restore your entries?`,
      okText: "Restore",
      cancelText: "Start fresh",
      centered: true,
      onOk: () => {
        const data = draft.data || {};
        form.setFieldsValue(data.values || {});
        setQuestionaire(Array.isArray(data.questionaire) ? data.questionaire : []);
        setCoordinates(data.coordinates || null);
        // The photo was uploaded on pick, so restore it from its URL (display-
        // only entry, mirroring the edit flow).
        setFileList(
          data.imageUrl
            ? [
                {
                  uid: "-1",
                  name: "property-image",
                  status: "done",
                  url: data.imageUrl,
                },
              ]
            : [],
        );
        restorePendingRef.current = false;
      },
      onCancel: () => {
        clearDraft(PROPERTY_DRAFT_KEY);
        restorePendingRef.current = false;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signUpModal, editingTask]);
  const { uploadFiles } = useFirebaseUpload();
  const navigate = useNavigate();

  const columns = [
    {
      title: "Property",
      dataIndex: "name",
      className: "role-name-column-header",
      align: "left",
      sorter: (a, b) => a.rawName.localeCompare(b.rawName),
    },
    {
      title: "Location",
      dataIndex: "location",
      align: "center",
      sorter: (a, b) => a.rawLocation.localeCompare(b.rawLocation),
    },
    {
      title: "Inspectors",
      key: "inspectors",
      align: "center",
      width: 300,
      className: "action-column-header",
      render: (_, r) => (
        <AssignInspectors
          value={Array.isArray(r.assignedInspectors) ? r.assignedInspectors : []}
          options={usersData}
          onSave={(uids) => saveInspectors(r.key, uids)}
        />
      ),
    },
    {
      // Merged cell: last *completed* date (lastInspectionDate) on top, and the
      // *current/open* inspection's status pill (latestStatus) beneath it.
      // Sorts by last-completed date.
      title: "Inspection",
      dataIndex: "inspection",
      align: "center",
      className: "action-column-header",
      // Never-inspected (null) sorts as oldest: top when ascending, bottom when
      // showing most-recent first.
      sorter: (a, b) => (a.lastInspectionTs || 0) - (b.lastInspectionTs || 0),
    },
    {
      // Open property_tasks count → click to the Tasks page filtered to this
      // property.
      title: "Tasks",
      dataIndex: "openTaskCount",
      align: "center",
      className: "action-column-header",
      sorter: (a, b) => a.openTaskCount - b.openTaskCount,
      render: (count, record) =>
        count > 0 ? (
          <Tag
            color="volcano"
            style={{ cursor: "pointer", margin: 0 }}
            onClick={() => navigate(`/tasks?propertyId=${record.key}`)}
          >
            {count} open
          </Tag>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      align: "center",
      className: "action-column-header",
    },
  ];

  const userNameById = useMemo(() => {
    const m = {};
    usersData.forEach((u) => {
      m[u._id] = u.name || u.email || u._id;
    });
    return m;
  }, [usersData]);

  const data = useMemo(
    () =>
      taskData
    ?.map((item) => ({
      key: item?._id,
      id: item?.id,
      rawName: item?.propertyName?.toLowerCase() || "",
      name: (
        <div
          className="task-link"
          style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "flex-start" }}
        >
          {/* Clicking the photo opens the enlarged preview (not navigation). */}
          <span
            onClick={(e) => e.stopPropagation()}
            style={{ display: "inline-flex", flexShrink: 0 }}
          >
            <Image
              style={{ borderRadius: "50%", width: 46, height: 46, objectFit: "cover" }}
              src={item?.propertyImages?.[0]}
              alt=""
            />
          </span>
          <div
            onClick={() => navigate(`/properties/${item?._id}`)}
            style={{
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              lineHeight: 1.25,
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 16 }}>
              {item?.propertyName || "N/A"}
            </span>
            {item?.ownerDetails?.ownerName && (
              <span style={{ fontSize: 13.5, color: "#8c8378" }}>
                {item.ownerDetails.ownerName}
              </span>
            )}
            {/* Beds / baths / kitchens — icon stats, like the mobile app. */}
            <span
              style={{
                display: "flex",
                gap: 16,
                fontSize: 14,
                color: "#6b6258",
                alignItems: "center",
                marginTop: 2,
              }}
            >
              <span
                style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
                title="Bedrooms"
              >
                <FaBed size={16} /> {item?.propertyObjects?.beds ?? 0}
              </span>
              <span
                style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
                title="Bathrooms"
              >
                <FaBath size={15} /> {item?.propertyObjects?.bath ?? 0}
              </span>
              <span
                style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
                title="Kitchens"
              >
                <FaKitchenSet size={15} /> {item?.propertyObjects?.kitchen ?? 0}
              </span>
            </span>
          </div>
        </div>
      ),
      email: item?.ownerDetails?.email?.toLowerCase() || "",
      owner: item?.ownerDetails?.ownerName || "N/A",
      rawOwner: (item?.ownerDetails?.ownerName || "").toLowerCase(),
      objects: `${item?.propertyObjects?.beds ?? "0"} / ${
        item?.propertyObjects?.bath ?? "0"
      } / ${item?.propertyObjects?.kitchen ?? "0"}`,
      location: item?.location ? (
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            (() => {
              const c = normalizeCoordinates(item?.coordinates);
              return c ? `${c.lat},${c.lng}` : item.location;
            })(),
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="location-link"
        >
          <EnvironmentOutlined />
          {item.location}
        </a>
      ) : (
        "N/A"
      ),
      rawLocation: (item?.location || "").toLowerCase(),
      inspection: (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span>
            <span style={{ color: "#999", marginRight: 4 }}>Last:</span>
            {item?.lastInspectionDate
              ? moment(item.lastInspectionDate).format("MM-DD-YYYY")
              : "N/A"}
          </span>
          <span>
            <span style={{ color: "#999", marginRight: 4 }}>Current:</span>
            {item?.latestStatus ? (
              <Tag
                color={statusColor(item?.latestStatus)}
                style={{ marginInlineEnd: 0 }}
              >
                {item?.latestStatus}
              </Tag>
            ) : (
              <Tag style={{ marginInlineEnd: 0 }}>no inspections</Tag>
            )}
          </span>
        </div>
      ),
      lastInspectionTs: item?.lastInspectionDate
        ? moment(item.lastInspectionDate).valueOf()
        : null,
      openTaskCount: item?.openTaskCount || 0,
      assignedInspectors: Array.isArray(item?.assignedInspectors)
        ? item.assignedInspectors
        : [],
      rawInspectors: (Array.isArray(item?.assignedInspectors)
        ? item.assignedInspectors
        : []
      )
        .map((uid) => userNameById[uid] || "")
        .join(" ")
        .toLowerCase(),
      actions: (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "edit",
                label: "Edit",
                onClick: () => handleEditTask(item),
              },
              {
                key: "delete",
                label: "Delete",
                danger: true,
                onClick: () => handleDeleteTask(item),
              },
            ],
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    }))
    .filter((item) => {
      // Dropdown: restrict to one inspector's properties.
      if (inspectorFilter && !item.assignedInspectors.includes(inspectorFilter)) {
        return false;
      }
      const lowerSearch = searchText?.toLowerCase() || "";
      return (
        item.rawName.includes(lowerSearch) ||
        item.email.includes(lowerSearch) ||
        item.rawInspectors.includes(lowerSearch)
      );
    }),
    [taskData, searchText, inspectorFilter, userNameById, navigate]
  );

  const getRowClassName = (record, index) => {
    return index % 2 === 0 ? "server-role-even-row" : "server-role-odd-row";
  };

  // Persist a property's assigned-inspector set (the source of truth for the
  // scheduler + the inspector's property PII access). Cloud Functions mirror it
  // onto open inspections and notify anyone newly added.
  const saveInspectors = async (propertyId, assignedInspectors) => {
    await updateDoc(doc(db, "properties", propertyId), { assignedInspectors });
  };

  // Add one inspector to every selected property (union — keeps existing
  // assignees), e.g. when onboarding an inspector across a portfolio.
  const bulkAssign = async () => {
    if (!bulkInspector || selectedRowKeys.length === 0) return;
    setIsLoading(true);
    try {
      await Promise.all(
        selectedRowKeys.map((pid) =>
          updateDoc(doc(db, "properties", pid), {
            assignedInspectors: arrayUnion(bulkInspector),
          }),
        ),
      );
      GreenNotify(
        `Assigned to ${selectedRowKeys.length} propert${
          selectedRowKeys.length === 1 ? "y" : "ies"
        }`,
      );
      setSelectedRowKeys([]);
      setBulkInspector(null);
    } catch (error) {
      RedNotify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTask = async (data) => {
    Modal.confirm({
      title: "Confirm Delete",
      content: `Are you sure you want to delete "${data.propertyName}"?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setIsLoading(true);
          await deleteDoc(doc(db, "properties", data._id));
          GreenNotify("Property deleted successfully");
          setUpdate((prev) => !prev);
        } catch (error) {
          RedNotify(error.message);
        } finally {
          setIsLoading(false);
        }
      },
    });
  };

  const handleAddQuestion = () => {
    setQuestionaire((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        area: "",
        question: "",
        image: null,
        note: "",
        checks: [{ label: "" }],
        resolutionSteps: [""],
      },
    ]);
  };

  const handleAddProblem = (qIndex) => {
    const updated = [...questionaire];
    updated[qIndex].checks.push({
      label: "",
    });
    setQuestionaire(updated);
  };

  const handleAddResolutionStep = (qIndex) => {
    const updated = [...questionaire];
    if (!updated[qIndex].resolutionSteps) {
      updated[qIndex].resolutionSteps = [];
    }
    updated[qIndex].resolutionSteps.push("");
    setQuestionaire(updated);
  };

  const handleRemoveResolutionStep = (qIndex, sIndex) => {
    const updated = [...questionaire];
    updated[qIndex].resolutionSteps.splice(sIndex, 1);
    setQuestionaire(updated);
  };

  // Apply a saved checklist template: deep-copy its items into the current
  // property's checklist with fresh ids and no answers. Editing them here does
  // not touch the saved template. Empty selection (clear) leaves items as-is.
  const handleApplyTemplate = (templateId) => {
    if (!templateId) return;
    const template = templates.find((t) => t._id === templateId);
    if (!template) return;
    const copied = (template.items || []).map((item, index) => ({
      id: `${Date.now()}-${index}`,
      area: item.area || "",
      question: item.question || "",
      image: item.image || null,
      note: "",
      checks: (item.checks || []).map((p) => ({
        label: p.label || "",
      })),
      resolutionSteps: item.resolutionSteps || [],
    }));
    setQuestionaire(copied);
    GreenNotify(`Applied "${template.name}" — edit items below as needed`);
  };

  // Open the "save current checklist as a reusable template" name prompt,
  // prefilling a sensible default from the property name being entered.
  const openSaveAsTemplate = () => {
    if (!questionaire || questionaire.length === 0) {
      RedNotify("Add at least one checklist item first");
      return;
    }
    const propName = form.getFieldValue("propertyName");
    setNewTemplateName(propName ? `${propName} checklist` : "");
    setSaveTemplateModal(true);
  };

  // Persist the property's current checklist as a new template. Uploads any
  // not-yet-saved item images first (and writes the resulting URLs back onto
  // the live questionaire so the subsequent property save reuses them rather
  // than re-uploading).
  const handleSaveAsTemplate = async () => {
    const name = newTemplateName.trim();
    if (!name) {
      RedNotify("Please enter a name for the checklist");
      return;
    }
    const checklistError = validateChecklistItems(questionaire);
    if (checklistError) {
      RedNotify(checklistError);
      return;
    }
    try {
      setIsLoading(true);
      const items = [];
      for (const [i, q] of questionaire.entries()) {
        if (q.imageFile) {
          const uploaded = await uploadFiles([q.imageFile]);
          if (!uploaded?.[0]?.image) {
            throw new Error(`Image upload failed for item #${i + 1}`);
          }
          q.image = uploaded[0].image;
          delete q.imageFile;
        }
        items.push({
          id: `${Date.now()}-${i}`,
          area: q.area || "",
          question: q.question || "",
          image: q.image || null,
          checks: (q.checks || []).map((p) => ({
            label: p.label || "",
          })),
          resolutionSteps: q.resolutionSteps || [],
          note: "",
        });
      }
      await addDoc(collection(db, "checklistTemplates"), {
        name,
        description: "",
        items,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      GreenNotify(`Saved "${name}" to Checklists`);
      setSaveTemplateModal(false);
      setNewTemplateName("");
      fetchChecklistTemplates();
    } catch (error) {
      RedNotify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadProps = {
    multiple: false,
    listType: "picture-card",
    fileList,
    // Upload the photo to storage as soon as it's picked (not at save time), so
    // the resulting URL can be drafted and survives a refresh/close. On failure
    // we keep the local File so onFinish's save-time fallback still uploads it.
    async onChange({ fileList: newFileList }) {
      const file = newFileList.slice(-1)[0];
      if (!file?.originFileObj) {
        setFileList(newFileList.slice(-1));
        return;
      }
      setFileList([{ uid: file.uid, name: file.name, status: "uploading" }]);
      try {
        const uploaded = await uploadFiles([file.originFileObj]);
        const url = uploaded?.[0]?.image;
        if (url) {
          setFileList([
            { uid: file.uid, name: file.name, status: "done", url },
          ]);
        } else {
          throw new Error("no url");
        }
      } catch (e) {
        // Keep the raw file (with originFileObj) so save-time upload retries it.
        setFileList([{ ...file, status: "done" }]);
        RedNotify("Photo upload failed — we'll retry when you save.");
      }
      triggerDraftSave(); // persist (or clear) the image url in the draft
    },
    beforeUpload: () => false,
    onRemove: () => {
      setFileList([]);
      triggerDraftSave();
      return true;
    },
  };

  const onFinish = async (values) => {
    setIsLoading(true);
    try {
      console.log("[CreateProperty] Submit started", {
        values,
        questionCount: questionaire?.length || 0,
        mainImageFileCount: fileList?.length || 0,
      });

      // Final backstop before persisting: the modal's Save gate already checks
      // this, but the checklist isn't an antd field so we re-assert it here using
      // the shared rule rather than trusting the caller.
      const checklistError = validateChecklistItems(questionaire);
      if (checklistError) {
        message.error(checklistError);
        return;
      }

      // Upload checklist item images
      for (const [qIndex, q] of questionaire.entries()) {
        if (q.imageFile) {
          console.log("[CreateProperty] Uploading checklist image", {
            qIndex,
            fileName: q.imageFile?.name,
            fileType: q.imageFile?.type,
            fileSize: q.imageFile?.size,
          });

          const uploaded = await uploadFiles([q.imageFile]);
          if (!uploaded?.[0]?.image) {
            throw new Error(
              `Checklist image upload failed for item #${qIndex + 1}`,
            );
          }
          q.image = uploaded[0].image;
          console.log("[CreateProperty] Checklist image uploaded", {
            qIndex,
            imageUrl: q.image,
          });
          delete q.imageFile;
        }
      }

      // Resolve the main property image. The photo normally uploads on pick, so
      // the fileList entry already carries its `url` — use it directly. Only
      // fall back to a save-time upload if an on-pick upload didn't complete
      // (the raw File is still attached as originFileObj).
      const existingPropertyImage = editingTask?.propertyImages?.[0] || "";
      let propertyImage = fileList[0]?.url || existingPropertyImage;
      if (fileList.length > 0 && fileList[0]?.originFileObj) {
        console.log("[CreateProperty] Uploading main property image (fallback)", {
          fileName: fileList[0].originFileObj?.name,
          fileType: fileList[0].originFileObj?.type,
          fileSize: fileList[0].originFileObj?.size,
        });
        const uploaded = await uploadFiles([fileList[0].originFileObj]);
        if (!uploaded?.[0]?.image) {
          throw new Error("Main property image upload failed");
        }
        propertyImage = uploaded[0].image;
        console.log("[CreateProperty] Main property image uploaded", {
          imageUrl: propertyImage,
        });
      }

      // Build a clean checklist template (no answers carried forward).
      const checklistTemplate = (questionaire || []).map((q) => ({
        id: q.id || Date.now().toString(),
        area: q.area || "",
        question: q.question || "",
        image: q.image || null,
        checks: (q.checks || []).map((p) => ({
          label: p.label || "",
        })),
        resolutionSteps: q.resolutionSteps || [],
        note: "",
      }));

      const formattedData = {
        propertyObjects: {
          beds: values.beds || "0",
          bath: values.bath || "0",
          kitchen: values.kitchen || "0",
          porch: values.porch || "0",
          lawn: values.lawn || "0",
        },
        propertyDescription: values.propertyDescription || "",
        location: values.location || "",
        coordinates: normalizeCoordinates(coordinates),
        ownerDetails: {
          email: values.ownerEmail || "",
          contact: values.ownerContact || "",
          ownerName: values.ownerName || "",
        },
        propertyName: values.propertyName || "",
        checklistTemplate,
        propertyImages: propertyImage ? [propertyImage] : [],
        assignedInspectors: Array.isArray(values.assignedInspectors)
          ? values.assignedInspectors
          : [],
        inspectionIntervalWeeks: values.inspectionIntervalWeeks || 1,
        timezone: values.timezone || "America/Denver",
      };

      if (editingTask?._id) {
        await handleUpdateTask(editingTask._id, formattedData);
      } else {
        await handleCreateTask(formattedData);
      }
    } catch (error) {
      console.error("Error saving property:", error);
      RedNotify(error?.message || "Property save failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTask = async (data) => {
    try {
      const propertiesCollectionRef = collection(db, "properties");
      await addDoc(propertiesCollectionRef, {
        ...data,
        lastInspectionDate: null,
        // Seed the cadence clock to next Monday in the property's own timezone so
        // weeklyInspectionReset starts creating inspections on the next cycle.
        nextInspectionDue: nextMondayInTz(data.timezone),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      GreenNotify("Property created successfully");
      clearDraft(PROPERTY_DRAFT_KEY); // saved for real — drop the local draft
      setUpdate((prev) => !prev);
      form.resetFields();
      setQuestionaire([]);
      setFileList([]);
      setCoordinates(null);
      setSignUpModal(false);
      setEditingTask(null);
    } catch (error) {
      RedNotify(error.message);
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setQuestionaire(task?.checklistTemplate || []);
    // Show the property's current image (if any) in the upload card. It's a
    // display-only entry (no originFileObj), so onFinish keeps the existing
    // image unless the user replaces it with a new upload.
    const existingImage = task?.propertyImages?.[0];
    setFileList(
      existingImage
        ? [
            {
              uid: "-1",
              name: "current-property-image",
              status: "done",
              url: existingImage,
            },
          ]
        : []
    );
    setCoordinates(normalizeCoordinates(task?.coordinates));
    form.setFieldsValue({
      ownerName: task?.ownerDetails?.ownerName || "",
      ownerContact: task?.ownerDetails?.contact || "",
      ownerEmail: task?.ownerDetails?.email || "",
      beds: task?.propertyObjects?.beds || undefined,
      bath: task?.propertyObjects?.bath || undefined,
      kitchen: task?.propertyObjects?.kitchen || undefined,
      porch: task?.propertyObjects?.porch || undefined,
      lawn: task?.propertyObjects?.lawn || undefined,
      propertyName: task?.propertyName || "",
      location: task?.location || "",
      propertyDescription: task?.propertyDescription || "",
      assignedInspectors: Array.isArray(task?.assignedInspectors)
        ? task.assignedInspectors
        : [],
      inspectionIntervalWeeks: task?.inspectionIntervalWeeks || 1,
      timezone: task?.timezone || "America/Denver",
    });
    setSignUpModal(true);
  };

  const handleUpdateTask = async (taskId, data) => {
    try {
      await updateDoc(doc(db, "properties", taskId), {
        ...data,
        updatedAt: new Date().toISOString(),
      });
      GreenNotify("Property updated successfully");
      setUpdate((prev) => !prev);
      resetCreateTaskState();
    } catch (error) {
      RedNotify(error.message);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const snapshot = await getDocs(collection(db, "users"));
      const users = snapshot.docs.map((doc) => ({
        _id: doc.id,
        ...doc.data(),
      }));
      // Only inspectors who have finished their mobile profile (isCompleted "2")
      // are assignable; exclude admins (they aren't field inspectors). Matches the
      // mobile assign screen's filter (assignTasks.js).
      const completedUser = users.filter(isAssignableInspector);
      setUsersData(completedUser);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };
  const fetchChecklistTemplates = async () => {
    try {
      const snapshot = await getDocs(collection(db, "checklistTemplates"));
      const rows = snapshot.docs.map((doc) => ({ _id: doc.id, ...doc.data() }));
      setTemplates(rows);
    } catch (error) {
      console.error("Error fetching checklist templates:", error);
    }
  };


  const resetCreateTaskState = () => {
    setSignUpModal(false);
    form.resetFields();
    setQuestionaire([]);
    setFileList([]);
    setCoordinates(null);
    setEditingTask(null);
  };

  // FR-8: stream properties + inspections so each property's latest inspection
  // status updates in real time, with no manual refresh.
  useEffect(() => {
    let propertyRows = [];
    let inspectionRows = [];
    let openTasksByProperty = {};
    let gotProperties = false;

    const recomputeStatus = () => {
      const latestByProperty = {};
      inspectionRows.forEach((insp) => {
        const pid = insp?.propertyId;
        if (!pid) return;
        const prev = latestByProperty[pid];
        if (!prev || (insp?.createdAt || "") > (prev?.createdAt || "")) {
          latestByProperty[pid] = insp;
        }
      });
      setTasksData(
        propertyRows.map((p) => ({
          ...p,
          latestStatus: latestByProperty[p._id]?.status || null,
          openTaskCount: openTasksByProperty[p._id] || 0,
        })),
      );
    };

    const unsubProperties = onSnapshot(
      collection(db, "properties"),
      (snap) => {
        propertyRows = snap.docs.map((d) => ({ _id: d.id, ...d.data() }));
        gotProperties = true;
        recomputeStatus();
        setLoading(false);
      },
      (error) => {
        console.error("Error streaming properties:", error);
        setLoading(false);
      },
    );

    const unsubInspections = onSnapshot(
      collection(db, "inspections"),
      (snap) => {
        inspectionRows = snap.docs.map((d) => d.data());
        if (gotProperties) recomputeStatus();
      },
      (error) => console.error("Error streaming inspections:", error),
    );

    // Open property_tasks per property → the "Tasks" count column. Open = not
    // done (mirrors isTaskOpen in helper/propertyTasks).
    const unsubTasks = onSnapshot(
      collection(db, "property_tasks"),
      (snap) => {
        const counts = {};
        snap.docs.forEach((d) => {
          const t = d.data();
          if (!t?.propertyId || t.status === "done") return;
          counts[t.propertyId] = (counts[t.propertyId] || 0) + 1;
        });
        openTasksByProperty = counts;
        if (gotProperties) recomputeStatus();
      },
      (error) => console.error("Error streaming property_tasks:", error),
    );

    return () => {
      unsubProperties();
      unsubInspections();
      unsubTasks();
    };
  }, []);

  // Users + checklist templates don't need realtime; (re)load on demand.
  useEffect(() => {
    fetchAllUsers();
    fetchChecklistTemplates();
  }, [update]);

  return (
    <div className="admin-products-main-container">
      <Loader loading={loading} />
      <PageHeader
        eyebrow="Operations"
        title="Properties"
        subtitle="Manage properties and assign inspections across your portfolio."
        actions={
          <>
            <Input
              className="sh-search"
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search property, owner email, or inspector"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder="Filter by inspector"
              value={inspectorFilter}
              onChange={(v) => setInspectorFilter(v ?? null)}
              style={{ minWidth: 200 }}
            >
              {usersData.map((u) => (
                <Select.Option key={u._id} value={u._id}>
                  {u.name || u.email || u._id}
                </Select.Option>
              ))}
            </Select>
            <Button
              type={selectMode ? "primary" : "default"}
              onClick={() => {
                // Leaving select mode clears any in-progress selection.
                if (selectMode) setSelectedRowKeys([]);
                setSelectMode((v) => !v);
              }}
            >
              {selectMode ? "Done" : "Select multiple"}
            </Button>
            <Button
              type="primary"
              className="sh-add-btn"
              icon={<PlusOutlined />}
              onClick={() => setSignUpModal(true)}
            >
              New property
            </Button>
          </>
        }
      />

      {selectedRowKeys.length > 0 && (
        <Space style={{ marginBottom: 12 }} wrap>
          <span>{selectedRowKeys.length} selected</span>
          <Select
            placeholder="Assign inspector…"
            value={bulkInspector}
            onChange={setBulkInspector}
            style={{ minWidth: 220 }}
            showSearch
            optionFilterProp="children"
          >
            {usersData.map((u) => (
              <Select.Option key={u._id} value={u._id}>
                {u.name || u.email || u._id}
              </Select.Option>
            ))}
          </Select>
          <Button
            type="primary"
            onClick={bulkAssign}
            disabled={!bulkInspector}
            loading={isloading}
          >
            Assign to selected
          </Button>
          <Button onClick={() => setSelectedRowKeys([])}>Clear</Button>
        </Space>
      )}

      <div
        className="server-roles-tb-main-container"
        style={{ overflowX: "auto" }}
      >
        <Table
          rowClassName={getRowClassName}
          rowKey="key"
          onRow={(record) => ({
            style: { cursor: "pointer" },
            onClick: (e) => {
              // Open the property on a row click — but let interactive controls
              // (actions menu, the tasks/status tags, inspector select, row
              // checkbox, the name link) handle their own clicks. The popup
              // classes matter: antd renders Select/Dropdown menus in a portal
              // on document.body, yet React still bubbles their clicks to this
              // row handler — so closest() must match the popup containers too,
              // or picking an inspector navigates away mid-edit.
              if (
                e.target.closest(
                  "button, a, input, .ant-tag, .ant-select, .ant-select-dropdown, .ant-dropdown, .ant-dropdown-trigger, .ant-checkbox-wrapper, .task-link",
                )
              ) {
                return;
              }
              navigate(`/properties/${record.key}`);
            },
          })}
          rowSelection={
            selectMode
              ? {
                  selectedRowKeys,
                  onChange: setSelectedRowKeys,
                }
              : undefined
          }
          columns={columns}
          dataSource={data}
          pagination={true}
          className="subscriptionapi-table"
        />
      </div>

      <CreateTaskModal
        open={signUpModal}
        editingTask={editingTask}
        onCancel={resetCreateTaskState}
        form={form}
        onValuesChange={triggerDraftSave}
        onFinish={onFinish}
        onFinishFailed={({ errorFields }) => {
          form.scrollToField(errorFields[0].name);
          RedNotify("Please fill all required fields correctly");
        }}
        statusOptions={statusOptions}
        bedOptions={bedOptions}
        bathOptions={bathOptions}
        kitchenOptions={kitchenOptions}
        porchOptions={porchOptions}
        lawnOptions={lawnOptions}
        usersData={usersData}
        uploadProps={uploadProps}
        fileList={fileList}
        questionaire={questionaire}
        setQuestionaire={setQuestionaire}
        coordinates={coordinates}
        setCoordinates={setCoordinates}
        handleAddQuestion={handleAddQuestion}
        handleAddProblem={handleAddProblem}
        handleAddResolutionStep={handleAddResolutionStep}
        handleRemoveResolutionStep={handleRemoveResolutionStep}
        templates={templates}
        onApplyTemplate={handleApplyTemplate}
        onSaveAsTemplate={openSaveAsTemplate}
        isloading={isloading}
        onCloseAndReset={resetCreateTaskState}
      />

      <Modal
        title="Save checklist as a template"
        open={saveTemplateModal}
        onCancel={() => setSaveTemplateModal(false)}
        onOk={handleSaveAsTemplate}
        okText="Save to Checklists"
        confirmLoading={isloading}
        maskClosable={false}
      >
        <p style={{ marginTop: 0, color: "#6b6258" }}>
          This copies the checklist items currently in the form into a reusable
          template under the Checklists tab.
        </p>
        <Input
          placeholder="e.g. Standard 3BR Residential"
          maxLength={60}
          value={newTemplateName}
          onChange={(e) => setNewTemplateName(e.target.value)}
          onPressEnter={handleSaveAsTemplate}
        />
      </Modal>
    </div>
  );
};

export default CompetitorList;
